import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';

// „Mit Apple anmelden“ im Browser über Apples JS-Bibliothek. Der Server prüft
// das Token gegen die Dienste-ID (APPLE_SERVICE_ID), wie in den Apps gegen die
// Bundle-ID.

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init(o: { clientId: string; scope: string; redirectURI: string; usePopup: boolean; state?: string }): void;
        signIn(): Promise<{
          authorization: { id_token: string; code: string };
          user?: { name?: { firstName?: string; lastName?: string }; email?: string };
        }>;
      };
    };
  }
}

const APPLE_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/de_DE/appleid.auth.js';

function loadAppleId(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.AppleID) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${APPLE_SRC}"]`);
    const script = existing ?? Object.assign(document.createElement('script'), { src: APPLE_SRC, async: true, defer: true });
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Apple-Skript nicht geladen')));
    if (!existing) document.head.appendChild(script);
  });
}

/** Button „Mit Apple anmelden“. Erscheint nur, wenn der Server eine Dienste-ID kennt. */
export function AppleButton({ mode, teamName, onToken, onError }: {
  mode: 'login' | 'register';
  teamName: string;
  onToken: () => void;
  onError: (msg: string) => void;
}) {
  const [config, setConfig] = useState<{ serviceId: string; redirectUri: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const teamRef = useRef(teamName);
  teamRef.current = teamName;

  useEffect(() => {
    api<{ appleServiceId: string | null; publicUrl?: string }>('/auth/config')
      .then((r) => setConfig(r.appleServiceId ? { serviceId: r.appleServiceId, redirectUri: `${r.publicUrl ?? window.location.origin}/` } : null))
      .catch(() => setConfig(null));
  }, []);

  async function signIn() {
    if (!config || busy) return;
    setBusy(true);
    try {
      await loadAppleId();
      if (!window.AppleID) throw new Error('Apple-Anmeldung ist nicht verfügbar');
      window.AppleID.auth.init({ clientId: config.serviceId, scope: 'name email', redirectURI: config.redirectUri, usePopup: true });
      const res = await window.AppleID.auth.signIn();
      // Den Namen schickt Apple nur bei der ersten Anmeldung mit
      const name = [res.user?.name?.firstName, res.user?.name?.lastName].filter(Boolean).join(' ').trim();
      const r = await api<{ token: string }>('/auth/apple', {
        method: 'POST',
        json: { identityToken: res.authorization.id_token, name: name || undefined, teamName: teamRef.current },
      });
      onToken();
    } catch (err) {
      const code = (err as { error?: string }).error;
      // Abbruch im Apple-Fenster ist kein Fehler
      if (code === 'popup_closed_by_user' || code === 'user_cancelled_authorize') return;
      onError(err instanceof Error ? err.message : 'Apple-Anmeldung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  }

  if (!config) return null;
  return (
    <button type="button" className="btn-apple" onClick={() => void signIn()} disabled={busy}>
      <Icon name="apple" size={18} />
      {mode === 'login' ? 'Mit Apple anmelden' : 'Mit Apple registrieren'}
    </button>
  );
}
